import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import ContentLoader from 'react-content-loader';

import {
    DropDownLinkAsRoute,
    DropDownLinkCommon,
    PrivateLayoutHeaderProps,
} from './types';
import styles from './PrivateLayoutHeader.module.scss';

import { useNavigate } from 'react-router-dom';

import defaultAvatar from 'ui/assets/images/user/user-placeholder.png';
import { Avatar, DropdownMenu } from 'ibnsina-ui';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectHeader } from 'features/layout/slice';
import { Button } from 'ibnsina-ui';
import { setActiveFrequency } from 'features/report/slice';

import settingsIcon from 'ui/assets/images/dropdown/settings.svg';
import passwordIcon from 'ui/assets/images/dropdown/lock.svg';
import historyIcon from 'ui/assets/images/dropdown/work_history.svg';
import logoutIcon from 'ui/assets/images/dropdown/logout.svg';
import { logOutFun, selectSupplierId, selectUser } from 'features/auth/slice';
import { addStatistic } from 'features/statistics/api';
import {
    StatisticActions,
    StatisticFunctions,
} from 'features/statistics/types';

const PrivateLayoutHeader = () => {
    const dispatch = useAppDispatch();

    let navigate = useNavigate();

    const userData = useAppSelector(selectUser);

    const supplierId = useAppSelector(selectSupplierId);

    const {
        icon,
        title,
        layout,
        theme,
        user,
        description,
        buttons,
        tabButtons,
    }: PrivateLayoutHeaderProps = useAppSelector(selectHeader);

    const dropdownLinks: (DropDownLinkCommon & DropDownLinkAsRoute)[] = [
        {
            icon: settingsIcon,
            label: 'Profile Settings',
            //href: '/update-user',
            isRoute: false,
            onClick: async () => {
                setIsDropdownOpen(false);

                navigate('/update-user');
            },
        },
        {
            icon: passwordIcon,
            label: 'Change Password',
            // href: '/change-password',
            isRoute: false,
            onClick: async () => {
                setIsDropdownOpen(false);

                navigate('/change-password');
            },
        },
        {
            icon: historyIcon,
            label: 'My Requests',
            //  href: '/myrequests/list',
            isRoute: false,
            onClick: async () => {
                setIsDropdownOpen(false);

                navigate('/myrequests/list');
            },
        },
        {
            icon: logoutIcon,
            label: 'Logout',
            linkColor: 'red' as const,
            isRoute: false,
            onClick: async () => {
                await addStatistic({
                    Action: StatisticActions.SignOut,
                    FunctionId: StatisticFunctions.SignOut,
                    SupplierId: supplierId ?? 0,
                });

                navigate('/');
                dispatch(logOutFun());
            },
        },
    ];

    const avatar = userData?.picture || defaultAvatar;

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const dropDownToggleButton = useRef<HTMLButtonElement>(null);
    const userWrapper = useRef<HTMLDivElement>(null);

    const HeaderClasses = classNames(styles.header, {
        [styles.layout_default]: layout === 'default' || !layout,
        [styles.layout_large]: layout === 'large',
        [styles.theme_default]: theme === 'default' || 'home' || !theme,
        [styles.theme_team]: theme === 'team',
    });

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    useEffect(() => {
        const clickOutside = (e: MouseEvent) => {
            if (
                !dropDownToggleButton?.current?.contains(
                    e.target as Node | null,
                ) &&
                !userWrapper?.current?.contains(e.target as Node | null)
            ) {
                if (isDropdownOpen) {
                    setIsDropdownOpen(false);
                }
            } else {
                return true;
            }
        };

        document.addEventListener('mousedown', clickOutside);

        return () => {
            document.removeEventListener('mousedown', clickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <header className={HeaderClasses}>
            <div className={styles.content_wrapper}>
                <div className={styles.content_icon}>
                    <img
                        src={icon}
                        alt="myimg"
                        className={styles.content_icon_img}
                    />
                </div>
                <div className={styles.content}>
                    <h1 className={styles.content_title}>{title}</h1>
                    {description && layout === 'large' ? (
                        <p className={styles.content_description}>
                            {description}
                        </p>
                    ) : null}
                    {buttons && layout === 'large' ? (
                        <div className={styles.content_buttons}>{buttons}</div>
                    ) : null}
                </div>
                {tabButtons ? (
                    <div className={styles['tab-buttons']}>
                        {tabButtons.map((tabButton, idx) => (
                            <div
                                key={`tab-button-${idx}`}
                                className={
                                    styles.button +
                                    (tabButton.isActive
                                        ? ` ${styles.active}`
                                        : '')
                                }>
                                {
                                    <Button
                                        color={tabButton.color}
                                        size={tabButton.size}
                                        design={tabButton.design}
                                        action={tabButton.action}
                                        onClick={() => {
                                            dispatch(
                                                setActiveFrequency(
                                                    tabButton.frequency,
                                                ),
                                            );
                                        }}>
                                        {tabButton.text}
                                    </Button>
                                }
                            </div>
                        ))}
                        {tabButtons.length ? null : (
                            <ContentLoader
                                speed={2}
                                width={270}
                                height={36}
                                viewBox="0 0 270 36"
                                backgroundColor="#ffffff"
                                foregroundColor="#ecebeb">
                                <rect
                                    x="0"
                                    y="0"
                                    rx="10"
                                    ry="10"
                                    width="90"
                                    height="36"
                                />
                                <rect
                                    x="90"
                                    y="0"
                                    rx="10"
                                    ry="10"
                                    width="90"
                                    height="36"
                                />
                                <rect
                                    x="180"
                                    y="0"
                                    rx="10"
                                    ry="10"
                                    width="90"
                                    height="36"
                                />
                            </ContentLoader>
                        )}
                    </div>
                ) : null}
            </div>
            <div className={styles.user_wrapper} ref={userWrapper}>
                <button
                    ref={dropDownToggleButton}
                    onClick={toggleDropdown}
                    className={styles.user}
                    title={user.firstName + ' ' + user.lastName}>
                    <Avatar
                        size="mini"
                        source={avatar}
                        className={styles.user_avatar}
                    />
                </button>
                <DropdownMenu
                    open={isDropdownOpen}
                    arrow="top-right"
                    links={dropdownLinks}
                />
            </div>
            {/* <div className={styles.user}>
                <img src={avatar} alt="" className={styles.user_avatar} />
            </div> */}

            {layout === 'large' ? (
                <object
                    aria-label="myimg"
                    type="image/svg+xml"
                    data={`${process.env.PUBLIC_URL}/assets/images/svgs/ibnsina-abstract.svg`}></object>
            ) : null}
        </header>
    );
};

export default PrivateLayoutHeader;
